import { AI_CHAT_STEP_KIND, AI_CHAT_STEP_STATUS } from '@/lib/constants';

// A lookup that only sets up context (passes) runs on the rolling line like thinking; a change
// shows what it was made with, a lookup does not.
const TOOLS = {
  search_help_center: { icon: 'book' },
  list_goals: { icon: 'goals' },
  get_goal: { icon: 'goals' },
  create_goal: { icon: 'edit', changes: true },
  create_goals: { icon: 'edit', changes: true },
  update_goal: { icon: 'edit', changes: true },
  check_in: { icon: 'edit', changes: true },
  list_goal_properties: { icon: 'tags', passes: true },
  list_saved_views: { icon: 'goals' },
  create_saved_view: { icon: 'goals', changes: true },
  list_check_ins: { icon: 'history' },
  list_check_in_templates: { icon: 'history' },
  create_check_in_template: { icon: 'history', changes: true },
  create_check_in_schedule: { icon: 'history', changes: true },
  list_plannings: { icon: 'plan' },
  create_planning: { icon: 'plan', changes: true },
  list_dashboards: { icon: 'dashboard' },
  get_dashboard: { icon: 'dashboard' },
  create_dashboard: { icon: 'dashboard', changes: true },
  add_dashboard_tile: { icon: 'dashboard', changes: true },
  add_dashboard_tiles: { icon: 'dashboard', changes: true },
  update_dashboard_tile: { icon: 'dashboard', changes: true },
  remove_dashboard_tile: { icon: 'dashboard', changes: true },
  list_cycles: { icon: 'calendar', passes: true },
  create_cycle: { icon: 'calendar', changes: true },
  whoami: { icon: 'user', passes: true },
  list_users: { icon: 'user' },
  list_teams: { icon: 'team' },
  create_team: { icon: 'team', changes: true },
};

export const stepIcon = (step) => {
  if (step.status === AI_CHAT_STEP_STATUS.failed) {
    return 'exclamation-circle';
  }
  switch (step.kind) {
    case AI_CHAT_STEP_KIND.thinking:
      return 'clock-circle';
    case AI_CHAT_STEP_KIND.webSearch:
      return 'global';
    default:
      return TOOLS[step.tool]?.icon ?? 'tool';
  }
};

export const stepLabel = (step, { t, te }) => {
  switch (step.kind) {
    case AI_CHAT_STEP_KIND.thinking:
      return t('aiChat.trace.thinking');
    case AI_CHAT_STEP_KIND.webSearch:
      return t('aiChat.trace.webSearch');
    default: {
      const key = `aiChat.trace.tools.${step.tool}`;
      return te(key) ? t(key) : t('aiChat.trace.tool', { name: step.tool });
    }
  }
};

const passes = (step) => step.status !== AI_CHAT_STEP_STATUS.failed
  && (step.kind === AI_CHAT_STEP_KIND.thinking || TOOLS[step.tool]?.passes === true);

const readable = (value) => (typeof value === 'string' ? value : JSON.stringify(value));

export const readableInput = (detail) => {
  try {
    const input = JSON.parse(detail);
    if (input === null || typeof input !== 'object' || Array.isArray(input)) {
      return detail;
    }
    return Object.entries(input).map(([key, value]) => `${key}: ${readable(value)}`).join('\n');
  } catch {
    return detail;
  }
};

const inputOf = (step) => (TOOLS[step.tool]?.changes && step.detail ? readableInput(step.detail) : '');

const queryOf = (step) => {
  if (step.kind === AI_CHAT_STEP_KIND.webSearch) {
    return step.detail || null;
  }
  try {
    const { query } = JSON.parse(step.detail || '{}');
    return typeof query === 'string' && query !== '' ? query : null;
  } catch {
    return null;
  }
};

// OpenAI opens each reasoning summary with a bold title, which says better than "Thinking"
// what the model is doing; Claude's summaries have none
export const splitThought = (thought) => {
  const match = /^\s*\*\*(.+?)\*\*\s*/.exec(thought);
  if (match === null) {
    return { title: null, body: thought };
  }
  return { title: match[1].trim(), body: thought.slice(match[0].length) };
};

const thinkingEntry = (step, thoughts) => {
  const { title, body } = splitThought(thoughts[thoughts.length - 1] ?? '');
  return {
    key: step.id,
    step,
    title,
    query: null,
    thoughts: [...thoughts.slice(0, -1), body].filter((thought) => thought.trim() !== ''),
    input: '',
    count: 1,
  };
};

const entry = (step, thoughts) => {
  if (step.kind === AI_CHAT_STEP_KIND.thinking) {
    return thinkingEntry(step, thoughts);
  }
  return {
    key: step.id,
    step,
    title: null,
    query: queryOf(step),
    thoughts,
    input: inputOf(step),
    count: 1,
  };
};

const repeats = (row, step) => row !== undefined
  && step.kind === AI_CHAT_STEP_KIND.tool
  && row.step.tool === step.tool
  && row.step.status !== AI_CHAT_STEP_STATUS.failed
  && step.status !== AI_CHAT_STEP_STATUS.failed;

const joinedQueries = (...queries) => {
  const distinct = [...new Set(queries.filter((query) => query !== null))];
  return distinct.length === 0 ? null : distinct.join(', ');
};

const merged = (row, step, thoughts) => ({
  ...row,
  step,
  query: joinedQueries(row.query, queryOf(step)),
  thoughts: [...row.thoughts, ...thoughts],
  input: [row.input, inputOf(step)].filter((input) => input !== '').join('\n'),
  count: row.count + 1,
});

const thoughtOf = (step) => (step.kind === AI_CHAT_STEP_KIND.thinking && step.detail ? [step.detail] : []);

// The thinking before a listed step is shown with that step, as the reason for it. OpenAI
// splits its reasoning into many items, most without a summary, which leaves nothing to show.
export const traceLayout = (steps, { running }) => {
  const rows = [];
  let thoughts = [];
  steps.forEach((step) => {
    if (passes(step)) {
      thoughts = [...thoughts, ...thoughtOf(step)];
      return;
    }
    const previous = rows[rows.length - 1];
    if (repeats(previous, step)) {
      rows[rows.length - 1] = merged(previous, step, thoughts);
    } else {
      rows.push(entry(step, thoughts));
    }
    thoughts = [];
  });

  const last = steps[steps.length - 1];
  if (running && last === undefined) {
    return { rows, current: entry({ id: 'waiting', kind: AI_CHAT_STEP_KIND.thinking, status: AI_CHAT_STEP_STATUS.running }, []) };
  }
  if (running) {
    const current = passes(last) ? entry(last, thoughtOf(last)) : null;
    return { rows, current };
  }
  if (thoughts.length > 0) {
    rows.push(entry({ id: `${last.id}-thoughts`, kind: AI_CHAT_STEP_KIND.thinking, status: AI_CHAT_STEP_STATUS.done }, thoughts));
  }
  return { rows, current: null };
};

export const upsertStep = (steps, step) => {
  const index = steps.findIndex((known) => known.id === step.id);
  if (index === -1) {
    return [...steps, step];
  }
  return steps.map((known, i) => (i === index ? step : known));
};

export const appendStepDetail = (steps, id, text) => steps
  .map((step) => (step.id === id ? { ...step, detail: `${step.detail ?? ''}${text}` } : step));

export const formatDuration = (ms, t) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes === 0) {
    return t('aiChat.trace.seconds', { seconds });
  }
  return t('aiChat.trace.minutes', { minutes, seconds });
};
